import { groupBy, keyBy } from "lodash";
import pluralize from "pluralize";
import React, { useCallback, useMemo } from "react";
import styled from "styled-components";

import { sortObjectivesItems } from "@examspark/common";
import {
  fromAWSDate,
  shortLocale,
} from "@examspark/common/dist/utils/coursePlanner";
import { useQueryGetCourseObjectives } from "@examspark/common-app";
import { RouterLink, Toolbar, VSpace } from "@examspark/common-ui";
import { colorFromTheme } from "@examspark/common-ui/dist/utils/color";
import {
  ObjectiveIndicator,
  YagDataStructure,
  getObjectiveCategory,
  getObjectiveCurriculumUsage,
} from "@examspark/customer/tcmpc";
import {
  Course,
  ObjectivesItem,
  TeksCategory,
  TeksCategoryList,
  TeksCategoryMeta,
} from "@examspark/types";

import { ClassroomPlannerYagProps, TeksFilter } from "../ClassroomPlannerYag";

import YagUnitStandards from "./YagUnitStandards";

const UnitItem = styled.div``;
const UnitTitle = styled(VSpace)`
  padding: 1rem;
  background-color: ${colorFromTheme("surface2")};
`;
const UnitBody = styled.div`
  padding: 1rem;
  color: #888;
`;

const UnitName = styled.div`
  font-size: 1.3rem;
  color: ${colorFromTheme("secondary", "textColor")};
`;
const UnitDateRange = styled.div`
  font-size: 1.1rem;
  color: #333;

  span {
    color: #777;
  }
`;

const StyledStandardsPlaceholder = styled.div`
  margin: 1rem;
  text-align: center;
  font-size: 1.1rem;
  color: #888;
`;

type SectionProps = {
  listStyle?: "grid" | "list";
};

const Section = styled.div<SectionProps>`
  background-color: ${colorFromTheme("surface0")};
  border: solid 1px #ddd;
  border-radius: 0.5rem;
  box-shadow: 0 0 1rem 0 rgba(0, 0, 0, 0.08);
  overflow: hidden;
  margin: 1rem;

  ${(props) =>
    props.listStyle === "grid" &&
    `
  min-height: 75rem;
  `}
`;

const SectionTitle = styled.div`
  background-color: ${colorFromTheme("surface0")};
  font-size: 1.6rem;
  color: #333;
  padding: 1.5rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.7rem;
`;
const SectionTitleLength = styled.div`
  font-weight: 300;
`;

type YagPeriodProps = {
  course: Course;
  linkContext: ClassroomPlannerYagProps["linkContext"];
  listStyle?: "grid" | "list";
  filters: Array<TeksFilter>;
  showStandardsInGroups: boolean;

  data: YagDataStructure["gradingPeriods"][number];
};

type TeksCategoryGroup = TeksCategory | "all" | "none";

const YagPeriod = ({
  course,
  linkContext,
  data,
  listStyle,
  filters,
  showStandardsInGroups,
}: YagPeriodProps) => {
  const objectivesQuery = useQueryGetCourseObjectives({
    courseId: course?.id,
    sk: `course#v0`,
  });
  const filterObjectives = useCallback(
    (items: ObjectivesItem[] | undefined | null) =>
      sortObjectivesItems(
        items?.filter((item) => {
          const usage = getObjectiveCurriculumUsage(item);
          const category = getObjectiveCategory(item.objective);
          if (usage == null || !filters.includes(usage)) return false;
          if (category != null && !filters.includes(category)) return false;
          if (category == null && !filters.includes("NO_CATEGORY"))
            return false;
          return true;
        }) ?? [],
      ),
    [filters],
  );
  const objectivesByUnit: Record<
    string,
    {
      id: string;
      objectivesByGroup: Record<
        TeksCategoryGroup,
        ObjectivesItem[] | undefined
      >;
    }
  > = useMemo(() => {
    if (objectivesQuery.data?.units == null) return {};
    return keyBy(
      objectivesQuery.data.units.map((u) => ({
        id: u.unit?.id ?? "",
        objectivesByGroup: groupBy(filterObjectives(u.unit?.objectives), (o) =>
          showStandardsInGroups
            ? (getObjectiveCategory(o.objective) ?? "none")
            : "all",
        ) as Record<TeksCategoryGroup, ObjectivesItem[] | undefined>,
      })),
      (ou) => ou.id,
    );
  }, [filterObjectives, showStandardsInGroups, objectivesQuery.data?.units]);

  const groups: TeksCategoryGroup[] = showStandardsInGroups
    ? [...TeksCategoryList, "none"]
    : ["all"];

  return (
    <Section listStyle={listStyle}>
      <SectionTitle>
        <div style={{ flex: 1 }}>{data?.name}</div>
        {data.durationInDays && (
          <SectionTitleLength>
            {data.durationInDays} {pluralize("day", data.durationInDays)}
          </SectionTitleLength>
        )}
      </SectionTitle>
      {data.units?.map((yagUnit) => {
        const unit = yagUnit.unit;

        if (unit == null) return null;

        const objectives = objectivesByUnit[unit.id];
        if (objectives == null) return null;

        return (
          <UnitItem key={yagUnit.unit.id}>
            <UnitTitle size="0.5rem">
              <UnitName>
                <RouterLink
                  to={
                    linkContext.type === "classroom"
                      ? `/classroom/${linkContext.classroom.id}/unit/${unit.id}`
                      : `/unit/${unit.id}`
                  }
                >
                  {unit.name}{" "}
                </RouterLink>{" "}
                {yagUnit.isContinued ? "(Continued)" : ""}
              </UnitName>
              <UnitDateRange>
                {yagUnit.startDate && yagUnit.endDate && (
                  <>
                    {shortLocale.format(fromAWSDate(yagUnit.startDate))} -{" "}
                    {shortLocale.format(fromAWSDate(yagUnit.endDate))}{" "}
                  </>
                )}
                <span>
                  ({yagUnit.daysInUnitInGradingPeriod}{" "}
                  {pluralize("Day", yagUnit.daysInUnitInGradingPeriod)})
                </span>
              </UnitDateRange>
            </UnitTitle>
            <UnitBody>
              {objectivesQuery.isLoading ? (
                <span>...</span>
              ) : groups.length > 0 ? (
                <VSpace>
                  <div>
                    {groups.map((group) => {
                      return (
                        <YagUnitStandards
                          key={group}
                          objectives={objectives.objectivesByGroup[group]}
                          unit={unit}
                          label={
                            group === "all" ? undefined : group === "none" ? (
                              "Other Student Expectations"
                            ) : (
                              <Toolbar>
                                {TeksCategoryMeta[group].label}
                                <ObjectiveIndicator value={group} />
                              </Toolbar>
                            )
                          }
                          showIndicator={!showStandardsInGroups}
                        />
                      );
                    })}
                  </div>
                </VSpace>
              ) : (
                <StyledStandardsPlaceholder>
                  No matching Standards for this unit.
                </StyledStandardsPlaceholder>
              )}
            </UnitBody>
          </UnitItem>
        );
      })}
    </Section>
  );
};

export default YagPeriod;
